import { useMemo, useState } from 'react';

import { isDefined } from '@e-socna/utils';
import type { Id } from '@e-socna/data-layer';

import type { FileStorage } from '../../../components/Editor/types/file-storage.type';
import type { DocumentResolversSettings } from '../../../components/Editor/types/document-resolvers-settings.type';
import type { ResolvableFieldSettings } from '../../../components/ResolvableFields/types/resolvable-field-settings.type';

import { useResolvableServiceSteps } from '../../ResolvableService/hooks/useSteps.hook';
import type { ResolvableServiceFormTab } from '../../ResolvableService/enums/tab.enum';

import { ResolvableServiceUsageFormTab } from '../enums/tab.enum';
import { ResolvableServiceUsageInfoFormStep } from '../steps/Info';
import { ResolvableServicesSelectionFormStep } from '../steps/Selection';
import type { ResolvableServiceUsageForm_ResolvableServiceUsage } from '../fragments/resolvable-service-usage.fragment';

interface Params {
  readonly documentResolversSettings: DocumentResolversSettings;
  readonly excludedIds?: readonly Id[];
  readonly initialValues: ResolvableServiceUsageForm_ResolvableServiceUsage;
  readonly formContext?: string;
  readonly resolvableFieldSettings: ResolvableFieldSettings;
  readonly stepIdsSubset?: readonly (
    ResolvableServiceUsageFormTab | ResolvableServiceFormTab
  )[];
  readonly storage: FileStorage;
}

export const useResolvableServiceUsageSteps = ({
  documentResolversSettings,
  excludedIds,
  storage,
  initialValues,
  resolvableFieldSettings,
  stepIdsSubset,
}: Readonly<Params>) => {
  const [isUsingResolvableServiceSteps, setIsUsingResolvableServiceSteps] =
    useState(isDefined(initialValues.specialService));

  const { allTabs: serviceAllTabs } = useResolvableServiceSteps({
    documentResolversSettings,
    formContext: 'specialService',
    isEnabled: isUsingResolvableServiceSteps,
    resolvableFieldSettings,
    storage,
  });

  const { steps, wizardChildren } = useMemo(() => {
    const resolvableServiceSelection = {
      step: {
        id: ResolvableServiceUsageFormTab.ResolvableServiceSelection,
        title: 'Sélection du Service',
        isEnabled: true,
      },
      child: (
        <ResolvableServicesSelectionFormStep
          excludedIds={excludedIds}
          id={ResolvableServiceUsageFormTab.ResolvableServiceSelection}
          key={ResolvableServiceUsageFormTab.ResolvableServiceSelection}
          setIsUsingResolvableServiceSteps={setIsUsingResolvableServiceSteps}
        />
      ),
    };

    const resolvableServiceUsageInfo = {
      step: {
        id: ResolvableServiceUsageFormTab.ResolvableServiceUsageInfo,
        title: 'Informations Prestation',
        isEnabled: true,
      },
      child: (
        <ResolvableServiceUsageInfoFormStep
          id={ResolvableServiceUsageFormTab.ResolvableServiceUsageInfo}
          key={ResolvableServiceUsageFormTab.ResolvableServiceUsageInfo}
          resolvableFieldSettings={resolvableFieldSettings}
        />
      ),
    };

    const allTabs = [
      [
        ResolvableServiceUsageFormTab.ResolvableServiceSelection,
        resolvableServiceSelection,
      ],
      ...serviceAllTabs,
      [
        ResolvableServiceUsageFormTab.ResolvableServiceUsageInfo,
        resolvableServiceUsageInfo,
      ],
    ] as const;

    const activeTabs = stepIdsSubset?.length
      ? allTabs.filter(([tab]) => stepIdsSubset.includes(tab))
      : allTabs;

    const steps = activeTabs.map(([, { step }]) => step);
    const wizardChildren = activeTabs.map(([, { child }]) => child);

    return { steps, wizardChildren };
  }, [excludedIds, resolvableFieldSettings, serviceAllTabs, stepIdsSubset]);

  return { steps, wizardChildren, isLoading: false };
};
